import type { BotCrawlCategory } from "~/lib/seo-bot-crawl"
import type { CheckerResponse, CheckerResult, ResultFilter } from "./checker-types"

export const categoryOrder: BotCrawlCategory[] = ["ai_search", "ai_user", "training", "search"]

export type RuleEvidence = {
  directive: "allow" | "disallow"
  lineLabel: string
  path: string
}

export type CategoryScore = {
  allowed: number
  category: BotCrawlCategory
  percent: number
  results: CheckerResult[]
  total: number
}

export type ScoreSummary = {
  allowed: number
  blocked: number
  categories: CategoryScore[]
  host: string
  percent: number
  total: number
  verdict: "open" | "closed" | "partial"
  verdictKind: "success" | "warning" | "danger"
}

const lineEvidencePattern = /^Line (\d+): (allow|disallow): (.*)$/i

function getHost(inputUrl: string) {
  try {
    return new URL(inputUrl).host
  } catch {
    return inputUrl
  }
}

function percent(allowed: number, total: number) {
  return total > 0 ? Math.round((allowed / total) * 100) : 0
}

export function parseRuleEvidence(result: CheckerResult): RuleEvidence {
  const match = lineEvidencePattern.exec(result.evidence)

  if (match?.[1] && (match[2] === "allow" || match[2] === "disallow")) {
    return {
      directive: match[2],
      lineLabel: `Line ${match[1]}`,
      path: match[3] || "/",
    }
  }

  return {
    directive: result.policy === "disallow" ? "disallow" : "allow",
    lineLabel: "Default",
    path: "/",
  }
}

export function getDirectiveCount(results: CheckerResult[]) {
  const lines = new Set<string>()

  for (const result of results) {
    const evidence = parseRuleEvidence(result)
    if (evidence.lineLabel !== "Default") {
      lines.add(evidence.lineLabel)
    }
  }

  return lines.size
}

export function matchesFilter(result: CheckerResult, filter: ResultFilter) {
  if (filter === "all") return true
  if (filter === "allow") return result.policy === "allow"
  return result.policy !== "allow"
}

function resolveVerdict(allowed: number, blocked: number): "open" | "closed" | "partial" {
  if (blocked === 0) return "open"
  if (allowed === 0) return "closed"
  return "partial"
}

function resolveVerdictKind(ratio: number): "success" | "warning" | "danger" {
  if (ratio >= 1) return "success"
  if (ratio <= 0) return "danger"
  return "warning"
}

export function buildScoreSummary(response: CheckerResponse): ScoreSummary {
  const categories = categoryOrder.map(category => {
    const results = response.results.filter(result => result.category === category)
    const allowed = results.filter(result => result.policy === "allow").length
    return {
      allowed,
      category,
      percent: percent(allowed, results.length),
      results,
      total: results.length,
    }
  })

  const total = response.results.length
  const allowed = response.results.filter(result => result.policy === "allow").length
  const blocked = total - allowed
  const ratio = total > 0 ? allowed / total : 0

  return {
    allowed,
    blocked,
    categories,
    host: getHost(response.inputUrl),
    percent: percent(allowed, total),
    total,
    verdict: resolveVerdict(allowed, blocked),
    verdictKind: resolveVerdictKind(ratio),
  }
}

export function isSearchOpenAndTrainingBlocked(summary: ScoreSummary) {
  const search = summary.categories.find(category => category.category === "search")
  const training = summary.categories.find(category => category.category === "training")

  return (
    search !== undefined &&
    search.total > 0 &&
    search.allowed === search.total &&
    training !== undefined &&
    training.total > 0 &&
    training.allowed === 0
  )
}
